/**
 * ElderKitty — the first Cat Elder of the Scrapyard (Babylon.js).
 *
 * Per the 2026-04-14 companion book canon (p. 14), Elder Kitty is
 * "a slight black ninja" — lean, near-black longhair, quiet and
 * watchful. He teaches the "Wisdom of Grounding" and gifts the
 * Resonant Drop. Where Grand Elder Leo is a cream-tabby wizard
 * presence, Elder Kitty is compact and stealthy — an old stealthy
 * elder who arrived before anyone saw him move.
 *
 * (Note: an earlier book.html draft described Kitty as a "rugged
 * battle-scarred tomcat." The new PDF canon reverses that. The
 * tomcat GLB is kept in assets/ for historical reference but is
 * no longer referenced.)
 *
 * Appears after the Apex puzzle in a cinematic Scrapyard encounter.
 * Primarily visual — interaction is driven by the CutsceneSystem.
 *
 * The scene owns one cool reveal light. The Elder keeps his feet on the deck.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { getAssetManager, InstantiatedModel } from "../../systems/AssetManager";
import { tagAs } from "../../levels/PlaceholderTag";

// Elder Kitty model generated via Meshy per 2026-04-14 companion book
// canon (p. 14): "a slight black ninja." Lean black longhair, nicked
// ears, watchful posture, worn dark cloth wrap. ~5.2 MB GLB via the
// standard text-to-3d preview → refine PBR → remesh 20k → 1k texture
// pipeline.
const ELDER_KITTY_MODEL = "/assets/models/npcs/elder_kitty_ninja.glb";

export class ElderKitty {
  public mesh: Mesh;
  private material: StandardMaterial;
  readonly modelReady: Promise<void>;
  private shadowCasters: AbstractMesh[] = [];
  private visualModel: InstantiatedModel | null = null;

  constructor(scene: Scene, position: Vector3, private readonly shadows?: ShadowGenerator) {

    // Body — near-black capsule (visual fallback until GLB loads).
    // Elder Kitty per new canon is a slight black ninja — the palette
    // is deep charcoal with a faint cool highlight, not warm ember.
    this.mesh = MeshBuilder.CreateCapsule(
      "elder_kitty",
      { height: 1.6, radius: 0.38, tessellation: 16, subdivisions: 6 },
      scene,
    );
    this.mesh.position = position.clone();

    this.material = new StandardMaterial("elder_kitty_mat", scene);
    // Deep near-black base with a touch of cool blue in the highlight,
    // matching "slight black ninja." Previously scorched brass (#8a5a33)
    // — that was the superseded tomcat palette.
    this.material.diffuseColor = Color3.FromHexString('#0b0b12');
    this.material.emissiveColor = new Color3(0.02, 0.02, 0.04);
    this.material.specularColor = Color3.Black();
    this.mesh.material = this.material;

    // Fire-and-forget GLB load. Capsule stays in the graph as the
    // parent transform for the authored mesh. Scene lighting supplies the reveal.
    this.modelReady = getAssetManager()
      .instantiateModel(scene, ELDER_KITTY_MODEL)
      .then((model) => {
        if (this.mesh.isDisposed()) {
          model.dispose();
          return;
        }
        this.visualModel = model;
        for (const root of model.rootNodes) {
          if (root instanceof TransformNode) {
            root.parent = this.mesh;
            tagAs(root, "final");
            root.position.copyFromFloats(0, 0, 0);
            // Keep the glTF handedness correction so the ninja faces the camera.
            root.computeWorldMatrix(true);
            for (const mesh of model.meshes) {
              mesh.computeWorldMatrix(true);
            }
            const bb = root.getHierarchyBoundingVectors(true);
            const targetBottom = this.mesh.position.y - 0.8;
            root.position.y = targetBottom - bb.min.y;
          }
        }
        for (const m of model.meshes) {
          m.isPickable = false; m.receiveShadows = true; tagAs(m, "final");
          if (m.getTotalVertices() > 0) { this.shadows?.addShadowCaster(m, false); this.shadowCasters.push(m); }
        }
        this.mesh.isVisible = false;
      })
      .catch((err) => {
        console.warn("[ElderKitty] Failed to load model", ELDER_KITTY_MODEL, err);
      });
  }

  dispose(): void {
    for (const mesh of this.shadowCasters) this.shadows?.removeShadowCaster(mesh, false);
    this.shadowCasters = [];
    this.visualModel?.dispose();
    this.visualModel = null;
    this.material.dispose();
    this.mesh.dispose();
  }
}
